import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { SimpleGrid } from '@chakra-ui/react';
import { add, differenceInDays } from 'date-fns';

import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import { quoteValidityPeriodSchema } from 'features/Work/form/optionForm.schema';
import { QuoteFormSchema } from 'features/Work/form/quoteForm.schema';

import locales from './locales';

export const DEFAULT_QUOTE_VALIDITY_DAYS = 4;

const QUOTE_VALIDITY_TIME_UNITS = [
  { label: locales.days, value: 'days' },
  { label: locales.weeks, value: 'weeks' },
  { label: locales.months, value: 'months' },
] satisfies {
  label: string;
  value: keyof Duration;
}[];

interface Args {
  period: number | null;
  startAt: number;
  timeUnit: keyof Duration;
}

export const getQuoteValidityDays: ({
  period,
  startAt,
  timeUnit,
}: Args) => number = ({ period, startAt, timeUnit }) => {
  if (period === null || period <= 0) {
    return -1;
  }
  if (timeUnit === 'days') {
    return period;
  }
  const startDate = new Date(startAt);
  const endDate = add(startDate, { [timeUnit]: period });
  return differenceInDays(endDate, startDate);
};

const ValidityPeriodInput = () => {
  const { getValues, setValue } = useFormContext<QuoteFormSchema>();

  /**
   * We can use `getValues` instead of `watch` here because `quoteValidityDays` is only used to init the data
   * @see https://react-hook-form.com/docs/useform/getvalues
   */
  const quoteValidityDays = getValues('settings.quoteValidityDays');

  const [period, setPeriod] = useState<number | null>(
    quoteValidityDays === null
      ? DEFAULT_QUOTE_VALIDITY_DAYS
      : quoteValidityDays,
  );

  const [timeUnit, setTimeUnit] = useState<keyof Duration>('days');

  const [hasValidityPeriod, setHasValidityPeriod] = useState(
    quoteValidityDays !== null,
  );

  const onChangeDisplayValidityPeriod = (checked: boolean) => {
    const startAt = getValues('startAt');
    const value = checked
      ? getQuoteValidityDays({ period, startAt, timeUnit })
      : null;

    setValue('settings.quoteValidityDays', value);
    setHasValidityPeriod(checked);
  };

  const onValueChange = (value: number | null) => {
    const startAt = getValues('startAt');

    setPeriod(value);
    setValue(
      'settings.quoteValidityDays',
      getQuoteValidityDays({ period: value, startAt, timeUnit }),
    );
  };

  const onSelect = (value: keyof Duration | null) => {
    if (value) {
      const startAt = getValues('startAt');

      setTimeUnit(value);
      setValue(
        'settings.quoteValidityDays',
        getQuoteValidityDays({ period, startAt, timeUnit: value }),
      );
    }
  };

  return (
    <SunshineCard.Group>
      <SunshineCard
        isChecked={hasValidityPeriod}
        isolation="isolate"
        onChange={(e) => onChangeDisplayValidityPeriod(e.target.checked)}
        title={locales.validityPeriod}
        variant="checkbox"
      />

      {hasValidityPeriod ? (
        <SunshineCard>
          <SimpleGrid columns={2} spacing="space-16">
            <Form.Input
              onChange={(e) => {
                const res = quoteValidityPeriodSchema
                  .catch(null)
                  .safeParse(e.target.value);

                if (res.success) {
                  onValueChange(res.data);
                }
              }}
              type="number"
              value={period ?? ''}
            />

            <Form.SunshineSelect
              onChange={(option) => (option ? onSelect(option.value) : null)}
              options={QUOTE_VALIDITY_TIME_UNITS}
              value={QUOTE_VALIDITY_TIME_UNITS.find(
                (option) => option.value === timeUnit,
              )}
            />
          </SimpleGrid>
        </SunshineCard>
      ) : null}
    </SunshineCard.Group>
  );
};

export default ValidityPeriodInput;
